import React, { Component } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { Info, Refresh, Save } from "@mui/icons-material";
import axios from "axios";
import Api from "../../../customlib/Api";

/**
 * Halaman Tele Auto Reply Settings.
 * Endpoint:
 *   GET  /api/tele/auto-reply/settings  -> { settings:[{key,value,description}], schema:{key:{type,label,...}} }
 *   POST /api/tele/auto-reply/settings  -> body: { settings:[{key,value}] }, all-or-nothing validation.
 */
class AutoReplyTeleSettings extends Component {
    state = {
        loading: false,
        saving: false,
        settings: [],        // rows dari DB + schema defaults
        schema: {},          // metadata per key
        edited: {},          // { key: newValue }
        fieldErrors: {},     // { key: "error message" } - validasi client
        snackbar: { open: false, message: "", severity: "success" },
        errorBanner: "",
    };

    componentDidMount() {
        this.loadSettings();
    }

    loadSettings = () => {
        this.setState({ loading: true, errorBanner: "" });
        axios
            .get(Api.teleAutoReplyListSettings(), Api.getRequestHeader())
            .then((res) => {
                const data = res.data || {};
                if (data.success) {
                    const settings = (data.data && data.data.settings) || [];
                    const schema = (data.data && data.data.schema) || {};
                    this.setState({
                        loading: false,
                        settings,
                        schema,
                        edited: {},
                        fieldErrors: {},
                    });
                } else {
                    this.setState({
                        loading: false,
                        errorBanner: data.message || "Gagal memuat settings",
                    });
                }
            })
            .catch((err) => {
                this.setState({
                    loading: false,
                    errorBanner:
                        (err.response && err.response.data && err.response.data.message) ||
                        err.message ||
                        "Network error",
                });
            });
    };

    getCurrentValue = (row) => {
        if (Object.prototype.hasOwnProperty.call(this.state.edited, row.key)) {
            return this.state.edited[row.key];
        }
        return row.value != null ? row.value : "";
    };

    setFieldValue = (key, value) => {
        const edited = { ...this.state.edited, [key]: value };
        const fieldErrors = { ...this.state.fieldErrors };
        const err = this.validateField(key, value);
        if (err) fieldErrors[key] = err;
        else delete fieldErrors[key];
        this.setState({ edited, fieldErrors });
    };

    validateField = (key, value) => {
        const meta = this.state.schema[key];
        if (!meta) return "";

        if (meta.required && (value === "" || value == null)) {
            return "Wajib diisi";
        }
        if (value === "" || value == null) return "";

        switch (meta.type) {
            case "enum": {
                const options = meta.options || [];
                if (options.length > 0 && !options.includes(value)) {
                    return `Harus salah satu dari: ${options.join(", ")}`;
                }
                return "";
            }
            case "minutes": {
                if (!/^-?\d+$/.test(String(value))) return "Harus angka bulat";
                const num = parseInt(value, 10);
                if (meta.min != null && num < meta.min) return `Minimum ${meta.min}`;
                if (meta.max != null && num > meta.max) return `Maksimum ${meta.max}`;
                return "";
            }
            case "wa_number": {
                const clean = String(value).replace(/[\s+\-]/g, "");
                if (!/^[0-9]{8,15}$/.test(clean)) return "Hanya digit, 8-15 karakter";
                return "";
            }
            default:
                return "";
        }
    };

    handleSave = () => {
        const fieldErrors = {};
        (this.state.settings || []).forEach((row) => {
            const value = this.getCurrentValue(row);
            const err = this.validateField(row.key, value);
            if (err) fieldErrors[row.key] = err;
        });

        if (Object.keys(fieldErrors).length > 0) {
            this.setState({
                fieldErrors,
                snackbar: {
                    open: true,
                    message: "Ada field yang belum valid. Mohon perbaiki dulu.",
                    severity: "error",
                },
            });
            return;
        }

        // Kirim semua row (pakai current value) — backend akan validasi ulang all-or-nothing.
        const payload = {
            settings: (this.state.settings || []).map((row) => ({
                key: row.key,
                value: String(this.getCurrentValue(row) ?? ""),
            })),
        };

        this.setState({ saving: true, errorBanner: "" });
        axios
            .post(Api.teleAutoReplyUpdateSettings(), payload, Api.getRequestHeaderJson())
            .then((res) => {
                const data = res.data || {};
                if (data.success) {
                    const settings = (data.data && data.data.settings) || [];
                    const schema = (data.data && data.data.schema) || this.state.schema;
                    this.setState({
                        saving: false,
                        settings,
                        schema,
                        edited: {},
                        fieldErrors: {},
                        snackbar: {
                            open: true,
                            message: "Settings berhasil disimpan",
                            severity: "success",
                        },
                    });
                } else {
                    this.setState({
                        saving: false,
                        errorBanner: data.message || "Gagal menyimpan settings",
                        snackbar: {
                            open: true,
                            message: data.message || "Gagal menyimpan",
                            severity: "error",
                        },
                    });
                }
            })
            .catch((err) => {
                const msg =
                    (err.response && err.response.data && err.response.data.message) ||
                    err.message ||
                    "Network error";
                this.setState({
                    saving: false,
                    errorBanner: msg,
                    snackbar: { open: true, message: msg, severity: "error" },
                });
            });
    };

    // ==============================================================
    // Render helpers
    // ==============================================================

    renderField = (row) => {
        const meta = this.state.schema[row.key] || { type: "string", label: row.key };
        const value = this.getCurrentValue(row);
        const error = this.state.fieldErrors[row.key] || "";
        const label = meta.label || row.key;

        const commonProps = {
            fullWidth: true,
            size: "small",
            value: value == null ? "" : value,
            onChange: (e) => this.setFieldValue(row.key, e.target.value),
            error: !!error,
            helperText: error || meta.description || row.description || "",
            variant: "outlined",
            InputLabelProps: { shrink: true },
        };

        switch (meta.type) {
            case "enum":
                return (
                    <FormControl fullWidth size="small" error={!!error}>
                        <InputLabel shrink>{label}</InputLabel>
                        <Select
                            value={value == null ? "" : value}
                            label={label}
                            onChange={(e) => this.setFieldValue(row.key, e.target.value)}
                            displayEmpty
                        >
                            {(meta.options || []).map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                    {opt}
                                </MenuItem>
                            ))}
                        </Select>
                        <Typography variant="caption" color={error ? "error" : "text.secondary"} sx={{ mt: 0.5, ml: 1.5 }}>
                            {error || meta.description || row.description || ""}
                        </Typography>
                    </FormControl>
                );
            case "minutes":
                return (
                    <TextField
                        {...commonProps}
                        type="number"
                        label={label}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">menit</InputAdornment>,
                            inputProps: { min: meta.min, max: meta.max, step: 1 },
                        }}
                    />
                );
            case "wa_number":
                return (
                    <TextField
                        {...commonProps}
                        label={label}
                        placeholder="6281234567890"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">📱</InputAdornment>
                            ),
                        }}
                    />
                );
            case "string":
            default:
                return <TextField {...commonProps} label={label} />;
        }
    };

    // ==============================================================
    // Group settings by schema.group
    // ==============================================================
    groupSettings = () => {
        const groups = {};
        const unknownKey = "Lainnya";

        (this.state.settings || []).forEach((row) => {
            const meta = this.state.schema[row.key];
            const g = (meta && meta.group) || unknownKey;
            if (!groups[g]) groups[g] = [];
            groups[g].push(row);
        });

        return groups;
    };

    render() {
        const { loading, saving, errorBanner, snackbar, edited, fieldErrors } = this.state;
        const dirty = Object.keys(edited).length > 0;
        const hasErrors = Object.keys(fieldErrors).length > 0;
        const grouped = this.groupSettings();

        return (
            <Box>
                <Grid container spacing={1} alignItems="center">
                    <Grid item sm={10} xs={8}>
                        <Typography variant="h6" fontWeight={600}>
                            Tele Auto Reply Settings
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Konfigurasi mode auto-reply untuk telecustomer (off/menu/single), throttle greeting, & nomor sales tele.
                        </Typography>
                    </Grid>
                    <Grid item sm={2} xs={4} textAlign="right">
                        <Tooltip title="Reload">
                            <span>
                                <IconButton onClick={this.loadSettings} disabled={loading || saving}>
                                    <Refresh />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Divider sx={{ mb: 2, mt: 1 }} />

                {loading && <LinearProgress sx={{ mb: 2 }} />}
                {errorBanner && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => this.setState({ errorBanner: "" })}>
                        {errorBanner}
                    </Alert>
                )}

                <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
                    Default mode adalah <b>off</b> (tidak mengirim balasan apapun). Perubahan mode langsung berlaku untuk pesan masuk berikutnya, tanpa perlu restart.
                </Alert>

                {Object.keys(grouped).map((groupName) => (
                    <Box key={groupName} sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: "primary.main" }}>
                            {groupName}
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Grid container spacing={2}>
                                {grouped[groupName].map((row) => (
                                    <Grid item xs={12} md={6} key={row.key}>
                                        {this.renderField(row)}
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                            <code style={{ fontSize: "10px" }}>{row.key}</code>
                                        </Typography>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    </Box>
                ))}

                <Box sx={{ position: "sticky", bottom: 0, bgcolor: "background.paper", py: 2, borderTop: "1px solid #eee", mt: 3 }}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} sm={8}>
                            {dirty && (
                                <Typography variant="body2" color="warning.main">
                                    Ada {Object.keys(edited).length} perubahan belum disimpan.
                                </Typography>
                            )}
                            {hasErrors && (
                                <Typography variant="body2" color="error">
                                    {Object.keys(fieldErrors).length} field masih invalid.
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={4} textAlign="right">
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                                onClick={this.handleSave}
                                disabled={!dirty || saving || hasErrors || loading}
                            >
                                {saving ? "Menyimpan..." : "Simpan Perubahan"}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => this.setState({ snackbar: { ...snackbar, open: false } })}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                    <Alert
                        severity={snackbar.severity}
                        onClose={() => this.setState({ snackbar: { ...snackbar, open: false } })}
                        sx={{ width: "100%" }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        );
    }
}

export default AutoReplyTeleSettings;
